import { Details } from '@core/ui';
import { Badge, Input, Modal, Row, Spin, Table, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import { useService } from '@core/inversify-react';
import { useBranchInspectionById } from '../hooks';
import { GetScansRepoType, IGetScansRepo } from '../repos';
import { IScan } from '../interfaces';

const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days} gün ${hours} saat`;
  } else if (hours > 0) {
    return `${hours} saat ${minutes} dəqiqə`;
  } else if (minutes > 0) {
    return `${minutes} dəqiqə ${seconds} saniyə`;
  } else {
    return `${seconds} saniyə`;
  }
};

const getStatusColor = (stateId: number) => {
  // Bu status ID-ləri backend-dən gələn actual ID-lərə görə düzəldiləcək
  if (stateId === 157) return 'success'; // Tamamlandı
  if (stateId === 156) return 'processing'; // Davam edir
  return 'default';
};

const getStatusIcon = (stateId: number) => {
  if (stateId === 157) return <Icons.CheckCircleOutlined />;
  if (stateId === 156) return <Icons.SyncOutlined spin />;
  return <Icons.ClockCircleOutlined />;
};

const getDeadlineStatus = (deadlineLeft: string, isCompleted: boolean) => {
  if (isCompleted) return 'success';
  if (deadlineLeft.includes('bitib')) return 'error';
  if (deadlineLeft.includes('saat') || deadlineLeft.includes('gün')) return 'warning';
  return 'success';
};

export const BranchInspectionDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const { data, isFetching } = useBranchInspectionById(id);
  const getScansRepo = useService<IGetScansRepo>(GetScansRepoType);

  const [scans, setScans] = useState<IScan[]>([]);
  const [scansLoading, setScansLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const onCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  const loadScans = useCallback(async () => {
    setScansLoading(true);
    const result = await getScansRepo.execute(id);

    if (result.status === 200) {
      setScans(result.response);
    }
    setScansLoading(false);
  }, [id, getScansRepo]);

  // Load scans
  useEffect(() => {
    loadScans();
  }, [loadScans]);

  // Filter scans by search text
  const filteredScans = useMemo(() => {
    if (!searchText.trim()) return scans;

    const searchLower = searchText.toLowerCase().trim();
    return scans.filter((scan) => (scan.trackCode?.toString() || '').toLowerCase().includes(searchLower) || (scan.barcode || '').toLowerCase().includes(searchLower));
  }, [scans, searchText]);

  return (
    <Modal width={1200} visible title='Filial yoxlanışı ətraflı məlumat' onCancel={onCancel} footer={null}>
      <Spin spinning={isFetching}>
        <Row gutter={[24, 24]}>
          {/* Ümumi məlumat */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='Ümumi məlumat'>
              <Details.Descriptions bordered={true} column={1} size='small'>
                <Details.Descriptions.Item label='Yoxlanış №'>
                  <strong>#{data?.id}</strong>
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Filial'>
                  <Tag color='blue' icon={<Icons.BankOutlined />}>
                    {data?.branch.name}
                  </Tag>
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Status'>
                  <Badge status={getStatusColor(data?.stateId || 0) as any} text={data?.state?.name || '-'} />
                  <span style={{ marginLeft: 8 }}>{getStatusIcon(data?.stateId || 0)}</span>
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Qeyd'>{data?.note || '-'}</Details.Descriptions.Item>
              </Details.Descriptions>
            </Details.Card>
          </Details.Col>

          {/* Tarixlər */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='Tarix məlumatları'>
              <Details.Descriptions bordered={true} column={1} size='small'>
                <Details.Descriptions.Item label='Son tarix'>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{data?.deadline.value}</span>
                    {data?.completedAt ? (
                      <Tag color='success' icon={<Icons.CheckCircleOutlined />}>
                        Tamamlanıb
                      </Tag>
                    ) : (
                      <Tag color={getDeadlineStatus(data?.deadline.left || '', false)} icon={<Icons.ClockCircleOutlined />}>
                        {data?.deadline.left}
                      </Tag>
                    )}
                  </div>
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Başlanma tarixi'>
                  {data?.startedAt ? (
                    <>
                      <Icons.PlayCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      {data.startedAt}
                    </>
                  ) : (
                    <Tag color='default'>Başlanmayıb</Tag>
                  )}
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Tamamlanma tarixi'>
                  {data?.completedAt ? (
                    <>
                      <Icons.CheckCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      {data.completedAt}
                    </>
                  ) : (
                    <Tag color='default'>Tamamlanmayıb</Tag>
                  )}
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Yaradılma tarixi'>
                  <Icons.CalendarOutlined style={{ marginRight: 8 }} />
                  {data?.createdAt}
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='Son yenilənmə'>
                  <Icons.SyncOutlined style={{ marginRight: 8 }} />
                  {data?.updatedAt}
                </Details.Descriptions.Item>
              </Details.Descriptions>
            </Details.Card>
          </Details.Col>

          {/* İstifadəçi məlumatları */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='İstifadəçi məlumatları'>
              <Details.Descriptions bordered={true} column={1} size='small'>
                <Details.Descriptions.Item label='Yaradan'>
                  <Tag color='purple' icon={<Icons.UserAddOutlined />}>
                    {data?.createdByUser.name}
                  </Tag>
                  <span style={{ marginLeft: 8, color: '#8c8c8c' }}>ID: {data?.createdByUser.id}</span>
                </Details.Descriptions.Item>
                <Details.Descriptions.Item label='İcraçı'>
                  {data?.executedByUser ? (
                    <>
                      <Tag color='green' icon={<Icons.UserOutlined />}>
                        {data.executedByUser.name}
                      </Tag>
                      <span style={{ marginLeft: 8, color: '#8c8c8c' }}>ID: {data.executedByUser.id}</span>
                    </>
                  ) : (
                    <Tag color='default'>Təyin edilməyib</Tag>
                  )}
                </Details.Descriptions.Item>
              </Details.Descriptions>
            </Details.Card>
          </Details.Col>

          {/* Statistika */}
          <Details.Col xs={24} lg={12}>
            <Details.Card title='Vaxt statistikası'>
              <Row gutter={16}>
                <Details.Col xs={24} sm={12}>
                  <div style={{ padding: '0 8px' }}>
                    <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 14 }}>
                      <Icons.FieldTimeOutlined style={{ marginRight: 8 }} />
                      İcraya başladı
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#262626' }}>
                      {data?.createdAt && data?.startedAt ? formatDuration(new Date(data.startedAt).getTime() - new Date(data.createdAt).getTime()) : '-'}
                    </div>
                    <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>Yaradıldıqdan, ilk scana başlayana qədər</div>
                  </div>
                </Details.Col>
                <Details.Col xs={24} sm={12}>
                  <div style={{ padding: '0 8px' }}>
                    <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 14 }}>
                      <Icons.HourglassOutlined style={{ marginRight: 8 }} />
                      İcra müddəti
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#3f8600' }}>
                      {data?.startedAt && data?.completedAt ? formatDuration(new Date(data.completedAt).getTime() - new Date(data.startedAt).getTime()) : '-'}
                    </div>
                    <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>Başladıqdan tamamlanana kimi</div>
                  </div>
                </Details.Col>
              </Row>
            </Details.Card>
          </Details.Col>

          {/* ID məlumatları */}
          <Details.Col xs={24}>
            <Details.Card title='Sistem identifikatorları'>
              <Details.Descriptions bordered={true} column={1} size='small'>
                <Details.Descriptions.Item label='Yoxlanış ID'>{data?.id}</Details.Descriptions.Item>
                <Details.Descriptions.Item label='Filial ID'>{data?.branchId}</Details.Descriptions.Item>
                <Details.Descriptions.Item label='Status ID'>{data?.stateId}</Details.Descriptions.Item>
                <Details.Descriptions.Item label='Yaradan istifadəçi ID'>{data?.createdBy}</Details.Descriptions.Item>
                <Details.Descriptions.Item label='İcraçı ID'>{data?.executedBy || '-'}</Details.Descriptions.Item>
              </Details.Descriptions>
            </Details.Card>
          </Details.Col>

          {/* Scan edilmiş bağlamalar */}
          <Details.Col xs={24}>
            <Details.Card
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Scan edilmiş bağlamalar</span>
                  <Tag color='blue'>{scans.length} ədəd</Tag>
                </div>
              }
            >
              <Input
                placeholder='İzləmə kodu və ya barkod ilə axtar...'
                prefix={<Icons.SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ marginBottom: 16 }}
                allowClear
              />
              <Table
                dataSource={filteredScans}
                rowKey='id'
                loading={scansLoading}
                pagination={{
                  pageSize: 50,
                  showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} bağlama`,
                  showSizeChanger: false,
                }}
                size='small'
                scroll={{ x: 800 }}
              >
                <Table.Column title='№' key='index' width={60} render={(_, __, index) => index + 1} />
                <Table.Column
                  title='İzləmə kodu'
                  dataIndex='trackCode'
                  key='trackCode'
                  render={(text) => (
                    <Tag color='blue' style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {text}
                    </Tag>
                  )}
                />
                <Table.Column
                  title='Barkod'
                  dataIndex='barcode'
                  key='barcode'
                  render={(text, record: IScan) => (
                    <Tag color={record.trendyol && record.trendyol > 0 ? 'purple' : 'default'} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {text || record.trackCode?.toString() || '-'}
                    </Tag>
                  )}
                />
                <Table.Column
                  title='Tip'
                  dataIndex='trendyol'
                  key='trendyol'
                  width={120}
                  render={(trendyol: number | null, record: IScan) => {
                    if (record.partnerId) {
                      return <Tag color='orange'>Partner</Tag>;
                    }
                    if (trendyol === 1) {
                      return <Tag color='purple'>Trendyol</Tag>;
                    }
                    if (trendyol === 2) {
                      return <Tag color='blue'>Temu</Tag>;
                    }
                    return <Tag color='green'>Findex</Tag>;
                  }}
                />
                <Table.Column
                  title='Scan tarixi'
                  dataIndex='scannedAt'
                  key='scannedAt'
                  width={180}
                  render={(text) => (
                    <>
                      <Icons.CalendarOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
                      {text}
                    </>
                  )}
                />
                <Table.Column title='Bağlama ID' dataIndex='declarationId' key='declarationId' width={100} render={(id) => <span style={{ color: '#8c8c8c' }}>{id}</span>} />
              </Table>
            </Details.Card>
          </Details.Col>
        </Row>
      </Spin>
    </Modal>
  );
};
